import { getConsumptionAPI, postConsumptionAPI } from "./httpUtils";

/**
 * Payload for saving user tile profile
 */
export interface UserTileProfilePayload {
    ID: number;          // Service ID
    ProfileJson: string; // JSON string containing segment and subSegment data
}

/**
 * Response from SaveOrUpdateUserTileProfile API
 */
export interface SaveUserTileProfileResponse {
    isSuccess: boolean;
    userId?: number;
    id?: number;
    message?: string;
    profileData?: string;
}

/**
 * Response from SaveUserTileProfile API
 */
export interface UserTileProfileResponse {
    userId: number;
    tileName: string;
    profileData: {
        segmentId: number;
        subSegmentId: number;
    };
    message: string;
}

export interface TileProfileResponse {
    isSuccess: boolean;
    profileJson: any | null;
}

/**
 * Fetch all segments from API
 * @param context ServiceContext for authentication
 * @returns Promise with segment data
 */
export const fetchSegments = async (context: any) => {
    try {
        if (context?.telemetryClient) {
            const response = await getConsumptionAPI(`/api/Segment/GetAll`, context.authClient);
            if (response.status >= 200 && response.status < 300) {
                return response.data; // Return raw data
            } else {
                throw new Error("Failed to fetch segments");
            }
        }
        return [];
    } catch (error) {
        console.error("Error fetching segments:", error);
        return [];
    }
};

/**
 * Fetch all sub-segments for a given segment ID
 * @param context ServiceContext for authentication
 * @param segmentId Segment ID
 * @returns Promise with sub-segment data
 */
export const fetchSubSegments = async (context: any, segmentId: number) => {
    try {
        if (context?.telemetryClient) {
            const response = await getConsumptionAPI(
                `/api/SubSegment/GetAllSubSegmentBySegmentId/${segmentId}`,
                context.authClient
            );
            if (response.status >= 200 && response.status < 300) {
                return response.data; // Return raw data
            } else {
                throw new Error("Failed to fetch sub-segments");
            }
        }
        return [];
    } catch (error) {
        console.error("Error fetching sub-segments:", error);
        return [];
    }
};

/**
 * Saves or updates the user tile profile data.
 * Makes a POST request to /api/SaveOrUpdateUserTileProfile.
 *
 * @param context - ServiceContext for authentication
 * @param payload - UserTileProfilePayload containing ID and ProfileJson
 * @returns Promise<SaveUserTileProfileResponse | null> - Returns the API response or null if failed
 */
export const saveUserTileProfileApi = async (context: any, payload: UserTileProfilePayload): Promise<SaveUserTileProfileResponse | null> => {
    try {
        // Validate payload
        if (!payload?.ID || !payload.ProfileJson) {
            console.error("Invalid payload: Missing required fields (ID or ProfileJson).");
            return null;
        }

        // Make API call
        const response = await postConsumptionAPI(`/api/UserTileProfiles/SaveOrUpdateUserTileProfile`, payload, context.authClient);

        // Check success status
        if (response && (response.status === 200 || response.status === 201)) {
            return response.data; // Return the actual response data
        } else {
            console.error("Failed to save profile. Status:", response.status);
            return null;
        }
    } catch (error: any) {
        console.error("Exception occurred while saving user tile profile:", error.message || error);
        return null;
    }
};

/**
 * Fetches the configurable tiles list from the API with session storage caching.
 *
 * @returns Promise<any[]> - Returns the list of configurable tiles.
 */
export const fetchConfigurableTilesList = async (context: any): Promise<any[]> => {
    // IMPORTANT: This session storage key is also used in V2_Tile.tsx (ifandelsecondition function)
    // If you modify this key, make sure to update it in V2_Tile.tsx as well to maintain consistency
    const cacheKey = "configurableTilesList";

    try {
        // Check session storage cache
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            return JSON.parse(cachedData);
        }

        if (context?.telemetryClient) {
            const response = await getConsumptionAPI(
                `/api/UserTileProfiles/GetConfigurableTilesList`,
                context.authClient
            );

            if (response.status >= 200 && response.status < 300) {
                const data = response.data;

                // Save to session storage for caching
                sessionStorage.setItem(cacheKey, JSON.stringify(data));

                return data;
            } else {
                throw new Error(`Failed to fetch configurable tiles list. Status: ${response.status}`);
            }
        }

        return [];
    } catch (error) {
        console.error("Error fetching configurable tiles list:", error);
        return [];
    }
};

/**
 * Fetches the user tile profile by serviceId and parses profileJson.
 *
 * @param context - ServiceContext for authentication
 * @param serviceId - The serviceId of the service/tile
 * @returns Promise<any | null> - Returns parsed profileJson or null if not found
 */
export const getUserTileProfilesById = async (context: any, serviceId: number): Promise<any | null> => {
    try {
        if (context?.telemetryClient) {
            const response = await getConsumptionAPI(
                `/api/UserTileProfiles/GetUserTileProfilesById/${serviceId}`,
                context.authClient
            );

            if (response?.status >= 200 && response.status < 300) {
                const data = response.data;

                //  Parse profileJson safely if it exists
                if (data?.profileJson) {
                    try {
                        return JSON.parse(data.profileJson);
                    } catch (parseError) {
                        console.error("Error parsing profileJson:", parseError);
                        return null;
                    }
                }

                return null; // No profileJson found
            } else if (response?.status === 204 || response?.status === 404) {
                return null;
            } else {
                throw new Error(`Failed to fetch user tile profile. Status: ${response?.status}`);
            }
        }
        return null;
    } catch (error) {
        console.error("Error fetching user tile profile by ID:", error);
        return null;
    }
};

/**
 * Retrieves user tile profile if the serviceId exists in configurable tiles list.
 *
 * Steps:
 * 1. Call fetchConfigurableTilesListApi
 *    - If serviceId not found → return { isSuccess: false, profileJson: null }
 * 2. If serviceId exists → call getUserTileProfilesByIdApi
 *    - If API fails or returns no data → return { isSuccess: false, profileJson: null }
 * 3. If profileJson exists → parse JSON and return { isSuccess: true, profileJson }
 *
 * @param context - ServiceContext for authentication
 * @param serviceId - The ID of the service/tile
 * @returns Promise<TileProfileResponse>
 */
export const getTileProfileIfExists = async (context: any, serviceId: number): Promise<TileProfileResponse> => {
    try {
        // Step 1: Fetch configurable tiles list
        const tilesList = await fetchConfigurableTilesList(context);
        if (!tilesList || tilesList.length === 0) {
            return { isSuccess: false, profileJson: null };
        }

        // Check if serviceId exists in tiles list
        const tileExists = tilesList.includes(serviceId);

        if (!tileExists) {
            return { isSuccess: false, profileJson: null };
        }

        // Step 2: Fetch and parse user tile profile
        const parsedProfile = await getUserTileProfilesById(context, serviceId);
        if (!parsedProfile) {
            return { isSuccess: true, profileJson: null };
        }

        return { isSuccess: true, profileJson: parsedProfile };
    } catch (error) {
        console.error("Error in getTileProfileIfExists:", error);
        return { isSuccess: false, profileJson: null };
    }
};

/**
 * Get JSON key based on dialogTitle for tile profile configuration
 * @param dialogTitle - The title of the dialog/tile
 * @returns The JSON key string or null if not found
 */
export const getJsonKeyForDialogTitle = (dialogTitle: string): string | null => {
    switch (dialogTitle) {
        case "Business Insights Requests":
            return "Business_Insights_Requests_ConfigurationJson";
        // Add more cases here as needed for other tiles
        default:
            console.warn(`No JSON key mapping found for dialogTitle: ${dialogTitle}`);
            return null; // Return null if no key found
    }
};

/**
 * Creates profile JSON payload for request type tiles (tiles with numberOfDropdown = 1)
 * @param serviceID - The service ID for the tile
 * @param firstSelectedOption - The selected dropdown option
 * @param dialogTitle - The title of the dialog/tile
 * @returns UserTileProfilePayload or null if validation fails
 */
export const createRequestTypeProfilePayload = (serviceID: number, firstSelectedOption: any, dialogTitle: string): UserTileProfilePayload | null => {
    // Get the JSON key based on dialogTitle
    const jsonKey = getJsonKeyForDialogTitle(dialogTitle);

    // If no key found, exit early
    if (!jsonKey) {
        return null;
    }

    // Extract last character and convert to number
    const lastChar = firstSelectedOption.key.slice(-1);
    const numericValue = Number(lastChar);

    // Validate that it's a valid number
    if (isNaN(numericValue)) {
        console.warn(`Invalid numeric value extracted from key: ${lastChar}`);
        return null; // Do not proceed if not a valid number
    }

    // Create JSON based on the determined key
    const Json = { [jsonKey]: numericValue };

    // Create ProfileJson as a JSON string
    const profileJson = JSON.stringify(Json);

    const payload: UserTileProfilePayload = {
        ID: serviceID,
        ProfileJson: profileJson
    };

    return payload;
};

/**
 * Normalizes dropdown options by extracting numeric keys from string keys.
 * Converts keys like "Enhancement to Existing Reporting0" to 0
 * @param options - Array of dropdown options with string keys
 * @returns Array of dropdown options with numeric keys
 */
export const normalizeDropdownKeys = (options: any[]) => {
    return options.map((option) => {
        // Extract last character from key and convert to number
        const lastChar = option.key.toString().slice(-1);
        const numericKey = Number(lastChar);

        // Return option with numeric key if valid, otherwise keep original
        return {
            ...option,
            key: !isNaN(numericKey) ? numericKey : option.key
        };
    });
};

/**
 * Returns the list of tile names for which the Sub-Segment selection must be hidden
 * (segment-selection popup + Edit Segment panel), regardless of what the Segment/Sub-Segment
 * API returns. The new segment model encodes the sub-segment into the segment value itself
 * (e.g. "MS Elevate", "SME&C - Corporate", "SME&C - SMB"), so no separate sub-segment is needed.
 *
 * Configured via the EnvironmentVariables entry keyed "HIDE_SUBSEGMENT_TILES" (a comma-separated
 * list of tile names) served by /api/User/GetEnvironmentVariables and cached in sessionStorage.
 * Falls back to an empty list when unset — i.e. no tile is force-hidden.
 */
export const getHideSubSegmentTiles = (): string[] => {
    try {
        const raw = sessionStorage.getItem("EnvironmentVariables");
        if (!raw) return [];

        const vars = JSON.parse(raw);
        if (!Array.isArray(vars)) return [];

        const entry = vars.find((v: any) => v?.key === "HIDE_SUBSEGMENT_TILES");
        if (!entry?.value) return [];

        return String(entry.value)
            .split(",")
            .map((t: string) => t.trim())
            .filter((t: string) => t.length > 0);
    } catch {
        return [];
    }
};

/**
 * Case-insensitive check: should the Sub-Segment selection be hidden for the given tile?
 * @param tileName - The tile / dialog title to check against the configured list.
 */
export const isSubSegmentHiddenForTile = (tileName?: string): boolean => {
    if (!tileName) return false;
    const normalized = tileName.trim().toLowerCase();
    return getHideSubSegmentTiles().some((t) => t.toLowerCase() === normalized);
};

// Generated by Copilot / updated on: 2025-11-02 | 220 lines added/updated | [Auto Generated] | Created comprehensive API utility module with TypeScript interfaces for UserTileProfilePayload and TileProfileResponse, implemented fetchSegments and fetchSubSegments functions with error handling, added saveUserTileProfileApi with validation, created fetchConfigurableTilesList with session storage caching, implemented getUserTileProfilesById with safe JSON parsing, and added getTileProfileIfExists as main orchestrator function for tile profile management | Time Taken 20 min
// updated on: 2026-07-07 | 8 lines added | [Auto Generated] | FYRO: added getHideSubSegmentTiles/isSubSegmentHiddenForTile helpers (HIDE_SUBSEGMENT_TILES EnvironmentVariable) so sub-segment can be force-hidden per tile | Time Taken 4 min
